import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TLeadTask, TPriority } from "@/models/tasks.model";

/**
 * Global "create a follow-up task?" prompt, shown right after a task is
 * completed anywhere in the app (Tasks page, dashboard card, lead details,
 * calendar, in-call checklist...). One host dialog subscribes to this store;
 * every completion site only reports here.
 *
 * The prompt opens as soon as the user clicks complete — while the request is
 * still in flight it shows a saving state (`saving: true`), so slow responses
 * still give immediate feedback. If the request fails, the site cancels and
 * the prompt closes alongside the optimistic-update rollback.
 */

export type TFollowUpSource = {
  taskId: number;
  leadId: number;
  leadName: string;
  /** Inherited by the follow-up task. */
  priority: TPriority;
  assignedToId: number;
  /** true while the completion request is still in flight. */
  saving: boolean;
};

type TPromptOpts = {
  /** For tasks fetched from lead-scoped endpoints, which omit lead info. */
  leadId?: number;
  leadName?: string;
};

type FollowUpPromptState = {
  source: TFollowUpSource | null;
  /** Opens the prompt (saving state) for a just-completed task. No-op for tasks without a lead. */
  promptForTask: (task: TLeadTask, opts?: TPromptOpts) => void;
  /** The completion request landed — flips the prompt out of its saving state. */
  markSaved: (taskId: number) => void;
  /** The completion request failed — closes the prompt (if still for this task). */
  cancelFor: (taskId: number) => void;
  close: () => void;
};

export const useFollowUpPromptStore = create<FollowUpPromptState>()(
  devtools(
    (set) => ({
      source: null,

      promptForTask: (task, opts) => {
        const leadId = opts?.leadId ?? task.lead?.id ?? task.leadId;
        if (leadId == null) return;
        const fromTask = [task.lead?.firstName, task.lead?.lastName].filter(Boolean).join(" ").trim();
        set({
          source: {
            taskId: task.id,
            leadId,
            leadName: opts?.leadName?.trim() || fromTask || `Lead #${leadId}`,
            priority: task.priority,
            assignedToId: task.assignedToId,
            saving: true,
          },
        });
      },

      markSaved: (taskId) =>
        set((s) =>
          s.source?.taskId === taskId ? { source: { ...s.source, saving: false } } : s
        ),

      cancelFor: (taskId) =>
        set((s) => (s.source?.taskId === taskId ? { source: null } : s)),

      close: () => set({ source: null }),
    }),
    { name: "follow-up-prompt" }
  )
);

// Imperative helpers for non-component call sites (data hooks).
export const promptFollowUpFor = (task: TLeadTask, opts?: TPromptOpts) =>
  useFollowUpPromptStore.getState().promptForTask(task, opts);
export const confirmFollowUpSaved = (taskId: number) =>
  useFollowUpPromptStore.getState().markSaved(taskId);
export const cancelFollowUpFor = (taskId: number) =>
  useFollowUpPromptStore.getState().cancelFor(taskId);
